/**
 * ExcalidrawDocumentViewer — rendu lecture-seule d'un document `.excalidraw.md`.
 *
 * @remarks
 * Généralisé depuis `PhaseExcalidrawPane` (tech-tests, `PhaseDocPane.tsx`) pour être partagé
 * avec `admin` (prévisualisation, SAAS-1025 follow-up) — deux features qui ne peuvent pas
 * s'importer entre elles. `ExcalidrawEditor` (~600 Ko) reste chargé en lazy ici, comme avant.
 */

import { lazy, Suspense, useMemo } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Spinner, Icon } from '@/shared/ui';
import { parseExcalidrawMd } from './parse-excalidraw-md';

const ExcalidrawEditor = lazy(() =>
  import('./ExcalidrawEditor').then((m) => ({ default: m.ExcalidrawEditor })),
);

function resolveTheme(theme: 'light' | 'dark' | 'system'): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export interface ExcalidrawDocumentViewerProps {
  content: string;
  theme: 'light' | 'dark' | 'system';
}

export function ExcalidrawDocumentViewer({ content, theme }: ExcalidrawDocumentViewerProps) {
  const excalidrawJson = useMemo(() => parseExcalidrawMd(content), [content]);
  const resolvedTheme = resolveTheme(theme);

  if (!excalidrawJson) {
    return (
      <div className="text-fg-secondary flex h-full items-center justify-center gap-2 p-6 text-sm">
        <Icon icon={TriangleAlert} size="lg" className="text-error" />
        Impossible de lire le fichier Excalidraw.
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center gap-2 p-6">
            <Spinner size="md" label="Chargement du dessin…" />
          </div>
        }
      >
        <ExcalidrawEditor
          key={content}
          docId="document-preview-readonly"
          content={excalidrawJson}
          onContentChange={() => undefined}
          theme={resolvedTheme}
          readOnly
        />
      </Suspense>
    </div>
  );
}
